import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from '@/lib/api';

export const APP_ACCENT_PRESETS = [
  { name: 'Amber Orange', hex: '#f59e0b', desc: 'VTC Original' },
  { name: 'Eiscyan (Neon)', hex: '#06b6d4', desc: 'Neon Eisblau' },
  { name: 'Smaragdgrün', hex: '#10b981', desc: 'Emerald Green' },
  { name: 'Apple Blau', hex: '#007aff', desc: 'Electric Blue' },
  { name: 'Cyber Violett', hex: '#8b5cf6', desc: 'Cyberpunk Purple' },
  { name: 'Sunset Pink', hex: '#ec4899', desc: 'Neon Sunset' },
  { name: 'Racing Rot', hex: '#ef4444', desc: 'Motorsport Red' },
  { name: 'Elektrogelb', hex: '#eab308', desc: 'High Voltage Yellow' },
];

export const DEFAULT_ACCENT_COLOR = '#f59e0b';

const getUserStorageKey = (u) => {
  const uid = u?.user_id || u?.id;
  return uid ? `opc_hub_accent_${uid}` : null;
};

export const hexToRgbValues = (hex) => {
  if (!hex || typeof hex !== 'string') return { r: 245, g: 158, b: 11 };
  let cleaned = hex.replace(/^#/, '').trim();
  if (cleaned.length === 3) {
    cleaned = cleaned[0] + cleaned[0] + cleaned[1] + cleaned[1] + cleaned[2] + cleaned[2];
  }
  if (cleaned.length !== 6) {
    return { r: 245, g: 158, b: 11 };
  }
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  return {
    r: isNaN(r) ? 245 : r,
    g: isNaN(g) ? 158 : g,
    b: isNaN(b) ? 11 : b,
  };
};

export const hexToHslValues = (hex) => {
  const { r: r255, g: g255, b: b255 } = hexToRgbValues(hex);
  const r = r255 / 255;
  const g = g255 / 255;
  const b = b255 / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};


export const getScopedThemeStyles = (hex) => {
  const safeHex = hex && typeof hex === 'string' && hex.startsWith('#') ? hex.trim() : DEFAULT_ACCENT_COLOR;
  const { r, g, b } = hexToRgbValues(safeHex);
  const { h, s, l } = hexToHslValues(safeHex);

  return {
    '--primary-rgb': `${r} ${g} ${b}`,
    '--app-accent-rgb': `${r}, ${g}, ${b}`,
    '--primary': `${h} ${s}% ${l}%`,
    '--primary-raw': safeHex,
    '--primary-color': safeHex,
    '--color-primary': safeHex,
    '--primary-glow': `rgba(${r}, ${g}, ${b}, 0.35)`,
    '--color-amber-300': safeHex,
    '--color-amber-400': safeHex,
    '--color-amber-500': safeHex,
    '--color-amber-600': safeHex,
  };
};

export const applyThemeCssVariables = (hex) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const { r, g, b } = hexToRgbValues(hex);
  const { h, s, l } = hexToHslValues(hex);

  // Space-separated for Tailwind rgb(var(--primary-rgb) / <alpha-value>)
  root.style.setProperty('--primary-rgb', `${r} ${g} ${b}`);
  // Space-separated for HSL
  root.style.setProperty('--primary', `${h} ${s}% ${l}%`);
  // Standard hex and aliases
  root.style.setProperty('--primary-raw', hex);
  root.style.setProperty('--primary-color', hex);
  root.style.setProperty('--color-primary', hex);
  root.style.setProperty('--color-amber-300', hex);
  root.style.setProperty('--color-amber-400', hex);
  root.style.setProperty('--color-amber-500', hex);
  root.style.setProperty('--color-amber-600', hex);
  // Comma-separated for rgba()
  root.style.setProperty('--app-accent-rgb', `${r}, ${g}, ${b}`);
  root.style.setProperty('--primary-glow', `rgba(${r}, ${g}, ${b}, 0.35)`);
};

const HubThemeContext = createContext(null);

export function HubThemeProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const isDrivershub = location.pathname.startsWith('/drivershub');
  const isDrivershubDashboard = isDrivershub &&
    !location.pathname.startsWith('/drivershub/login') &&
    !location.pathname.startsWith('/drivershub/register');
  const userId = user?.user_id || user?.id || null;

  // Cleanup legacy global storage key that was shared between users
  useEffect(() => {
    try {
      localStorage.removeItem('openpipeclub_hub_accent_color');
      localStorage.removeItem('opc_hub_accent_color');
    } catch (e) {}
  }, []);

  // Determine accent color for the currently logged in user
  const getUserColor = useCallback(() => {
    if (!user) return DEFAULT_ACCENT_COLOR;
    if (user.accent_color && typeof user.accent_color === 'string' && user.accent_color.startsWith('#')) {
      return user.accent_color.trim();
    }
    const storageKey = getUserStorageKey(user);
    if (storageKey && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem(storageKey);
      if (saved && saved.startsWith('#')) return saved.trim();
    }
    return DEFAULT_ACCENT_COLOR;
  }, [user]);

  const [accentColor, setAccentColorState] = useState(getUserColor);
  const [saving, setSaving] = useState(false);
  const saveTimeoutRef = useRef(null);

  // Sync color when user logs in, switches accounts, or profile loads from backend
  useEffect(() => {
    const nextColor = getUserColor();
    setAccentColorState(nextColor);
    const storageKey = getUserStorageKey(user);
    if (storageKey && nextColor !== DEFAULT_ACCENT_COLOR) {
      try {
        localStorage.setItem(storageKey, nextColor);
      } catch (e) {}
    }
  }, [user, getUserColor]);

  // Route-aware CSS variable application:
  // - In DriversHub: apply the logged-in driver's personal color
  // - On Public Pages: STRICTLY enforce Open Pipe Club's default brand Amber (#f59e0b)
  // Route-aware CSS variable application:
  // - In DriversHub Dashboard: apply the logged-in driver's personal color
  // - On Public Pages & Public Auth Pages (Login/Register): STRICTLY enforce Open Pipe Club's default brand Amber (#f59e0b)
  useEffect(() => {
    if (isDrivershubDashboard) {
      applyThemeCssVariables(accentColor);
      if (typeof document !== 'undefined') {
        document.documentElement.classList.add('drivershub-theme');
      }
    } else {
      applyThemeCssVariables(DEFAULT_ACCENT_COLOR);
      if (typeof document !== 'undefined') {
        document.documentElement.classList.remove('drivershub-theme');
      }
    }
  }, [isDrivershubDashboard, accentColor]);

  const updateAccentColor = useCallback((newColor, persistToBackend = true) => {
    if (!newColor || typeof newColor !== 'string') return;
    const formatted = newColor.startsWith('#') ? newColor.trim() : `#${newColor.trim()}`;
    
    // Immediate state update
    setAccentColorState(formatted);

    // Save under the active user's unique storage key
    const storageKey = getUserStorageKey(user);
    if (storageKey && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(storageKey, formatted);
      } catch (e) {}
    }

    // If currently in DriversHub, apply live
    if (isDrivershubDashboard) {
      applyThemeCssVariables(formatted);
    }

    // Persist to user's individual profile in D1 database
    if (persistToBackend && user) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      setSaving(true);
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          const res = await api.auth.updateProfile({ accent_color: formatted });
          // Update cached user in localStorage so AuthContext on reload knows new color
          try {
            const cached = localStorage.getItem('opc_cached_user');
            if (cached) {
              const parsed = JSON.parse(cached);
              parsed.accent_color = formatted;
              localStorage.setItem('opc_cached_user', JSON.stringify(parsed));
            }
          } catch (e) {}
          if (res?.user && user) {
            user.accent_color = formatted;
          }
        } catch (err) {
          console.warn('Failed to save accent_color to driver profile:', err);
        } finally {
          setSaving(false);
        }
      }, 350);
    }
  }, [user, isDrivershub]);

  const resetAccentColor = useCallback(() => {
    updateAccentColor(DEFAULT_ACCENT_COLOR, true);
  }, [updateAccentColor]);

  // Current RGB for canvas / animated backgrounds in DriversHub
  const activeDisplayColor = isDrivershubDashboard ? accentColor : DEFAULT_ACCENT_COLOR;
  const rgb = hexToRgbValues(activeDisplayColor);

  const value = {
    accentColor,
    isDrivershub,
    setAccentColor: updateAccentColor,
    updateAccentColor,
    resetAccentColor,
    presets: APP_ACCENT_PRESETS,
    defaultColor: DEFAULT_ACCENT_COLOR,
    rgb,
    saving,
  };

  return <HubThemeContext.Provider value={value}>{children}</HubThemeContext.Provider>;
}

export function useHubTheme() {
  const ctx = useContext(HubThemeContext);
  if (!ctx) {
    throw new Error('useHubTheme must be used within HubThemeProvider');
  }
  return ctx;
}
